import { z } from "zod";
import type { TFunction } from "i18next";

/**
 * legalEntity.name/address не обязательны на уровне модели (платформа без
 * Paynet законно живёт без реквизитов — см. PlatformModel.legalEntity),
 * поэтому здесь НЕТ `required: true` в схеме формы: платформа-настройки —
 * одна форма на все параметры с одной кнопкой сохранения, и required
 * поле без `noValidate` у FormBuilder заблокировало бы сохранение вообще
 * всех настроек для каждой уже существующей платформы, у которой
 * реквизиты просто не заполнены.
 *
 * Вместо этого — парная проверка: сохранить половину блока нельзя (он всё
 * равно не отрисуется в футере без name), но и заполнять его не обязан
 * никто. `.passthrough()` — валидируем только legalEntity, остальные поля
 * формы проходят как есть.
 */
export function makePlatformSettingsValidationSchema(t: TFunction) {
  return z
    .object({
      legalEntity: z
        .object({
          name: z.string().optional(),
          address: z.string().optional(),
          registrationNumber: z.string().optional(),
          email: z.string().optional(),
          phone: z.string().optional(),
        })
        .optional(),
    })
    .passthrough()
    .superRefine((values, ctx) => {
      const legalEntity = values.legalEntity;
      if (!legalEntity) return;

      const hasName = Boolean(legalEntity.name?.trim());
      const hasAddress = Boolean(legalEntity.address?.trim());

      if (hasName && !hasAddress) {
        ctx.addIssue({
          code: "custom",
          path: ["legalEntity", "address"],
          message: t("platformSettings.form.legalEntityAddressRequiredError"),
        });
      }

      if (hasAddress && !hasName) {
        ctx.addIssue({
          code: "custom",
          path: ["legalEntity", "name"],
          message: t("platformSettings.form.legalEntityNameRequiredError"),
        });
      }
    });
}
